// A tiny module-level flag + pub/sub — same lightweight pattern already
// used by usePushNotifications' debug log store. It exists purely so
// NoticeModal (mounted at the top of the whole app) and AccountSwitcher
// (mounted deep inside a settings/profile screen) can coordinate without
// needing a shared parent, Context provider, or prop drilling for
// something this small and this rarely touched.
//
// The problem this solves: entering a wrong password in AccountSwitcher's
// "Add Account" / "Switch" flow never touches the CURRENT account's real
// session (see the validationClient comments in AccountSwitcher.tsx) — so
// while that password modal is open, the original account is still fully
// authenticated and still on a dashboard route. useActiveNotices is
// therefore completely correct to want to show a real notice for that
// original account — but doing so pops the notice modal right on top of
// the account-switcher's own password modal, which looks exactly like a
// notice appearing before anyone's logged in. This flag lets
// useActiveNotices simply wait until the switcher is done.

type Listener = (isBusy: boolean) => void;

let isBusy = false;
const listeners = new Set<Listener>();

/** Called by AccountSwitcher whenever its Add Account modal is open or a
 * switch attempt is in flight. */
export function setAccountSwitcherBusy(value: boolean) {
  if (isBusy === value) return;
  isBusy = value;
  listeners.forEach((listener) => listener(isBusy));
}

export function getAccountSwitcherBusy(): boolean {
  return isBusy;
}

/** Subscribe to changes. Immediately calls back with the current value,
 * then again on every change. Returns an unsubscribe function. */
export function subscribeAccountSwitcherBusy(listener: Listener): () => void {
  listeners.add(listener);
  listener(isBusy);
  return () => listeners.delete(listener);
}
